import { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { format } from 'date-fns/format';
import { getDataQAForInput } from 'components/Inputs/helpers';
import useField from 'hooks/useField';
import { TextSpan } from 'app/typography';
import EditableText from 'components/Kizen/EditableText';
import { TextEllipsis } from 'components/Kizen/Table';
import StylePassthrough from 'components/Kizen/StylePassthrough';
import SelectOverlay from '../SelectOverlay';
import { getDateOption, timeOptions } from '../DateTimeInput';
import Select from '../Select';
import { TextInputInlineWrapper } from './TextInput/styles';
import DateTextInlineInput from 'components/Inputs/inline/TextInput/presets/DatePicker';
import { Menu as DateMenu } from '../DateInput';
import { TrackingMenuList } from '../Select/customize';
import {
  modalNudgePopperUp,
  nudgePopperUp,
  DateInputInlineSelect,
} from './DateInput';
import { FIELD_TYPES } from 'utility/constants';
import { useDateTooltip } from '../hooks/useDateTooltip';
import { useKeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { useCustomSelectMenuProps } from 'hooks/keyboardEventHandler/useCustomSelectMenuProps';
import { useRecordDetailCustomScript } from '@kizenapps/engine/react';
import { getAdornments } from 'components/Inputs/Adornments/util';
import { toastVariant, useToast } from 'components/ToastProvider';
import { useTranslation } from 'react-i18next';
import { WithIconDataAdornments } from '../Adornments/WithIconDataAdornments';
import { ADORNMENT_VARIANTS } from '../Adornments/constants';

const Layout = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
`;

const TooltipHitBox = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
`;

const ShowWithoutHover = styled(StylePassthrough)`
  ${({ show }) =>
    !show &&
    css`
      visibility: hidden;
      tr:hover &,
      .InlineHoverable:hover & {
        visibility: visible;
      }
    `}
`;

const ReadonlyDateTimeContainer = styled(TextEllipsis)`
  > span {
    font-size: inherit;
  }

  span:first-child {
    min-width: 85px;
  }

  span:last-child {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const ReadonlyDateTime = ({ date, time, ...rest }) => {
  if (!date?.label && !time?.label) {
    return (
      <ReadonlyDateTimeContainer {...rest}>
        <span>&#8212;</span>
      </ReadonlyDateTimeContainer>
    );
  }

  return (
    <ReadonlyDateTimeContainer {...rest}>
      {date?.label ? <span>{date.label}</span> : <span>&#8212;</span>}
      {date && time && <AtText>at</AtText>}
      {time?.label ? <span>{time?.label}</span> : <span>&#8212;</span>}
    </ReadonlyDateTimeContainer>
  );
};

const getUpdatedDate = ({ date, time }) => {
  if (!date && !time) {
    return null;
  }
  return new Date(
    `${date?.label || format(new Date(), 'MM/dd/yyyy')} ${
      time ? time.value : ''
    }`
  );
};

const AtText = styled(TextSpan)`
  margin: 0 5px;
`;

export default function DateTimeInputInline({
  value = null,
  onChange = null,
  onSubmit = null,
  autoFocus = null,
  onAutoFocus = null,
  submitUnchanged = null,
  readOnly = false,
  inModal,
  dateMenuProps,
  timeMenuProps,
  'data-field-type': qaFieldType,
  'data-field-id': qaFieldId,
  placeholder = FIELD_TYPES.DateTime.placeholder,
  tooltipLabel,
  showDateTooltip = false,
  field,
  shouldFocusNext,
  entityId,
  fieldId: fieldIdProp,
  objectId,
  activityId,
  keyListenerId,
  adornments = [],
  ...others
}) {
  const fieldId = useMemo(
    () => keyListenerId || `${field?.id}-${entityId}`,
    [field, entityId, keyListenerId]
  );
  // Setup state
  const dateRef = useRef();
  const timeRef = useRef();
  const timeSelectRef = useRef();
  const [editing, setEditing] = useState(true);
  const [dayPlaceholder, timePlaceholder] = placeholder.split(' ');
  const prevDate = useRef(null);
  const [showDate, setShowDate] = useState(false);
  const [showTime, setShowTime] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [stagedValue, setStagedValue] = useField(value);
  const displayValue = onSubmit ? stagedValue : value;
  const [stagedDateValue, setStagedDateValue] = useField(null);
  const [date, setDate] = useState(() => getDateOption(displayValue, true));
  const [showToast] = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    setDate(getDateOption(displayValue, true));
  }, [displayValue]);

  const time = useMemo(
    () => getDateOption(displayValue, false),
    [displayValue]
  );
  // Support autoFocus
  const { assignFieldHandle, getKeyListenersProps } = useKeyBoardContext();
  const onAutoFocusRef = useRef();
  onAutoFocusRef.current = onAutoFocus;

  useEffect(() => {
    if (autoFocus && !getKeyListenersProps) {
      setShowDate(true);
      if (dateRef.current) onAutoFocusRef.current(dateRef.current);
    }
  }, [autoFocus, getKeyListenersProps]);

  // Handlers

  const handleChange = async (val, { isDate }) => {
    const updatedDate =
      isDate && val.date === null
        ? null
        : getUpdatedDate({
            date: isDate ? val : date,
            time: isDate ? time : val,
          });
    if (onSubmit) {
      setStagedValue(updatedDate);
    } else {
      setShowDate(false);
      setShowTime(false);
    }
    if (onChange) {
      await onChange(updatedDate);
    }
  };

  const handleSubmit = async (v, useIncomeValue = false) => {
    if (submitUnchanged || Number(stagedValue) !== Number(value) || v) {
      try {
        const newValue = v
          ? getUpdatedDate({
              date: getDateOption(v, true),
              time: time,
            })
          : date.date;
        setSubmitting(true);
        prevDate.current = getDateOption(useIncomeValue ? v : newValue, true);
        setShowDate(false);
        const res = await onSubmit(useIncomeValue ? v : newValue);
        setDate(getDateOption(useIncomeValue ? v : newValue, true));
        return res;
      } catch (err) {
        setStagedValue(value);
        return false;
      } finally {
        setSubmitting(false);
        prevDate.current = null;
      }
    }
    return false;
  };

  const handleHide = async () => {
    setShowDate(false);
    setShowTime(false);
    if (onSubmit) {
      await handleSubmit();
    }
  };

  const [dateTooltipProps, dateTooltip] = useDateTooltip({
    date: showDateTooltip && date?.date,
    predicate: tooltipLabel,
  });
  const focusedRef = useRef(null);
  const selectRef = useRef(null);

  const { setMenuIsOpen, ...customSelectMenuProps } = useCustomSelectMenuProps(
    selectRef,
    {
      shouldUseKeyboardEventHandle: true,
      onBlur: () => {
        timeRef.current?.querySelector('input')?.focus();
        setShowTime(true);
        setShowDate(false);
        focusedRef.current = 'time';
      },
      ...others,
    }
  );

  const onParentControl = (e) => {
    if (e?.key === 'Escape') {
      setEditing(false);
      setShowTime(false);
      setShowDate(false);
      return true;
    }
    setShowDate(false);
    if (e?.shiftKey) {
      if (focusedRef.current === 'time') {
        setShowTime(false);
        if (e?.key !== 'Enter') {
          dateRef.current?.querySelector('input')?.focus();
          focusedRef.current = 'date';

          if (stagedDateValue) {
            setStagedValue(
              getUpdatedDate({
                date: { label: stagedDateValue },
                time: time,
              })
            );
          }
        }

        return true;
      } else {
        focusedRef.current = null;
        setShowTime(false);
        setEditing(false);
        return false;
      }
    } else {
      if (focusedRef.current === 'date') {
        timeRef.current?.querySelector('input')?.focus();
        setShowTime(true);
        focusedRef.current = 'time';
        if (stagedDateValue) {
          setStagedValue(
            getUpdatedDate({
              date: { label: stagedDateValue },
              time: time,
            })
          );
        }
        return true;
      } else {
        focusedRef.current = null;
        setShowTime(false);
        setEditing(false);
        return false;
      }
    }
  };

  const onFocusedDate = (e) => {
    if (focusedRef.current !== 'time') {
      setEditing(true);
      setShowTime(false);
      focusedRef.current = 'date';
    }
  };

  assignFieldHandle(fieldId, {
    customUp: () => {
      if (focusedRef.current === 'date') {
        setShowDate(true);
      }
    },
    customDown: () => {
      if (focusedRef.current === 'date') {
        setShowDate(true);
      }
    },
    disabled: readOnly,
    shouldFocusNext: shouldFocusNext,
  });

  const dateTimeAdornments = getAdornments(adornments, {
    fieldType: 'datetime',
  });

  const [executeScript, internalState] = useRecordDetailCustomScript({
    entityId,
    objectId,
    onError: (e) => {
      showToast({
        message: `${t('Action could not run')}: ${e?.message}`,
        variant: toastVariant.FAILURE,
      });
    },
  });

  const hasAdornments =
    dateTimeAdornments.length > 0 &&
    (date.date || time.date || date.value || time.value);

  // Render and display
  if (readOnly) {
    return (
      <>
        {dateTooltip}
        <WithIconDataAdornments
          hasAdornments={hasAdornments}
          adornments={dateTimeAdornments}
          scriptArgs={{
            value,
            fieldId: field?.id ?? fieldIdProp,
            fieldType: field?.fieldType,
            objectId,
            entityId,
            activityId,
          }}
          disabled={internalState.pending}
          executeScript={executeScript}
          variant={ADORNMENT_VARIANTS.inline}
        >
          <ReadonlyDateTime
            {...others}
            {...dateTooltipProps}
            date={date}
            time={time}
          />
        </WithIconDataAdornments>
      </>
    );
  }

  const onClickDateInput = () => {
    setShowDate(true);
    focusedRef.current = 'date';
  };

  const onClickTimeInput = () => {
    setShowTime(true);
    focusedRef.current = 'time';
    getKeyListenersProps?.(fieldId)?.onFocus(fieldId);
  };

  const onHideDateOverlay = async (e) => {
    if (e && !dateRef.current.contains(e.target)) {
      await handleHide();
    }
  };

  return (
    <>
      <Layout {...getDataQAForInput(qaFieldId, qaFieldType)} {...others}>
        <WithIconDataAdornments
          hasAdornments={hasAdornments}
          adornments={dateTimeAdornments}
          scriptArgs={{
            value,
            fieldId: field?.id ?? fieldIdProp,
            fieldType: field?.fieldType,
            objectId,
            entityId,
            activityId,
          }}
          disabled={internalState.pending}
          executeScript={executeScript}
          variant={ADORNMENT_VARIANTS.inline}
        >
          {dateTooltip}
          <TooltipHitBox {...dateTooltipProps}>
            <TextInputInlineWrapper ref={dateRef} button={false}>
              <DateTextInlineInput
                field={field}
                entityId={entityId}
                editing={editing}
                ref={selectRef}
                className="ViewText DateText"
                value={
                  submitting ? prevDate.current?.label : date ? date.label : ''
                }
                disabled={submitting}
                onSubmit={handleSubmit}
                callback={setStagedDateValue}
                isDateInput
                showError
                onClick={onClickDateInput}
                {...getDataQAForInput('date-input', qaFieldType)}
                {...others}
                placeholder={dayPlaceholder}
                {...getKeyListenersProps?.(fieldId)}
                onFocused={onFocusedDate}
                onParentControl={onParentControl}
                shouldFocusNext={shouldFocusNext}
                keyListenerId={keyListenerId}
                inModal={inModal}
              />
            </TextInputInlineWrapper>
            <ShowWithoutHover show={date && time}>
              <AtText
                color={
                  submitting
                    ? 'var(--color-font-placeholder)'
                    : 'var(--color-font-primary)'
                }
              >
                at
              </AtText>
            </ShowWithoutHover>
            <ShowWithoutHover show={date && time} ref={timeRef}>
              <TextInputInlineWrapper button={false}>
                <EditableText
                  className="ViewText"
                  value={time?.label || '—'}
                  disabled={submitting}
                  readOnly
                  onClick={onClickTimeInput}
                  {...getDataQAForInput('time-input', qaFieldType)}
                />
              </TextInputInlineWrapper>
            </ShowWithoutHover>
          </TooltipHitBox>
        </WithIconDataAdornments>
      </Layout>
      <SelectOverlay
        show={showDate}
        target={dateRef.current}
        onHide={onHideDateOverlay}
        popperConfig={inModal ? modalNudgePopperUp : nudgePopperUp}
        iDoNotWantToMountTwice
      >
        <DateInputInlineSelect
          value={date}
          onChange={(val) => handleChange(val, { isDate: true })}
          sizing={false}
          components={{ Menu: DateMenu }}
          handeClose={() => setShowDate(false)}
          {...dateMenuProps}
          {...customSelectMenuProps}
        />
      </SelectOverlay>
      <SelectOverlay
        show={showTime}
        target={timeRef.current}
        onHide={async () => {
          await handleHide();
        }}
        popperConfig={inModal ? modalNudgePopperUp : nudgePopperUp}
        {...getDataQAForInput(qaFieldId, `${qaFieldType}-time-select`)}
      >
        <Select
          ref={timeSelectRef}
          value={time?.value ? time : null}
          options={timeOptions}
          onChange={(val) => handleChange(val, { isDate: false })}
          components={{
            MenuList: TrackingMenuList,
          }}
          handeClose={() => setShowTime(false)}
          {...timeMenuProps}
          placeholder={timePlaceholder}
          {...getKeyListenersProps?.(fieldId)}
          tabSelectsValue={true}
        />
      </SelectOverlay>
    </>
  );
}
